/**
 * Studio room — wraps WS + WebRTC. Renders Lobby (phase=lobby/ended)
 * or StudioLive (phase=live/paused) based on room state.
 *
 * P3 acceptance: trainer + case can lobby up, consent, hit Start, both
 * see studio mode with placeholder content.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth.js";
import { api } from "../lib/api.js";
import { useStudioRoom, type Role, type ServerMsg, type RoomState, type TaskState } from "../hooks/useStudioRoom.js";
import { useStudioMedia } from "../hooks/useStudioMedia.js";
import { useRecorder } from "../hooks/useRecorder.js";

export default function Studio() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();

  // Hooks must run unconditionally — Studio is route-gated by RequireAuth
  // so user/sessionId will be defined under normal flow. Use safe fallbacks
  // for the hook inputs and bail in render below.
  const role: Role = user?.role === "case" ? "case" : "trainer";

  // Signal handler bridge: WS messages forwarded to media hook
  const signalHandlerRef = useRef<((msg: ServerMsg) => void) | null>(null);

  // Live metrics keyed by source — populated from case-side sub-app via WS
  const [metrics, setMetrics] = useState<Record<string, { value: number; ts: number }>>({});
  // Sub-app lifecycle state forwarded from case via subapp_state — used
  // for trainer-side preflight gating (e.g. block start_task on soramynd
  // tasks until case has paired the EEG device). 2026-05-09 fix.
  const [caseSubappState, setCaseSubappState] = useState<string | null>(null);
  // Raw EEG preview ring buffer keyed by channel label. Updated from
  // `raw_preview` BC payloads (case-side cognihome-soramynd eegBridge).
  // 2026-05-09 fix: replaces the long-standing "P5 cross-repo 待接"
  // placeholder once both sides are wired end-to-end.
  const rawEegRef = useRef<Record<string, number[]>>({});
  const [rawEegSps, setRawEegSps] = useState<number>(100);
  const [rawEegTs,  setRawEegTs]  = useState<number>(0);

  const handleMessage = useCallback((msg: ServerMsg) => {
    if (msg.type === "webrtc_offer" || msg.type === "webrtc_answer" || msg.type === "ice_candidate") {
      signalHandlerRef.current?.(msg);
    }
    if (msg.type === "metric") {
      console.log("[Studio WS metric]", msg.source, "=", msg.value);
      setMetrics(prev => ({ ...prev, [msg.source]: { value: msg.value, ts: msg.ts ?? Date.now() } }));
    }
    if (msg.type === "subapp_state") {
      console.log("[Studio WS subapp_state]", msg.state, msg.source ?? "", msg.task ?? "");
      setCaseSubappState(msg.state);
    }
    if (msg.type === "raw_preview") {
      console.log("[Studio WS raw_preview]",
        `${msg.channels.length}ch × ${msg.samples[0]?.length ?? 0}samp @${msg.sps}sps`);
      // case → DO → trainer relay (added 2026-05-09 because BC doesn't
      // cross browsers; previous attempt to subscribe parent-side BC
      // never received a payload when trainer + case were on different
      // machines).
      const buf = rawEegRef.current;
      const max = 6 * (msg.sps ?? 100);
      for (let i = 0; i < msg.channels.length; i++) {
        const ch = msg.channels[i]!;
        if (!buf[ch]) buf[ch] = [];
        const incoming = msg.samples[i] ?? [];
        for (const s of incoming) buf[ch].push(s);
        if (buf[ch].length > max) buf[ch].splice(0, buf[ch].length - max);
      }
      if (msg.sps) setRawEegSps(msg.sps);
      if (msg.ts)  setRawEegTs(msg.ts);
    }
  }, []);

  // (raw_preview now arrives via WS in handleMessage above; the
  // browser-only BroadcastChannel subscriber that briefly lived here
  // was a dead end since BC is per-browser, never crossed the trainer
  // ↔ case machines that typically run on different LANs.)

  const { state, connected, error, send } = useStudioRoom({
    sessionId: sessionId ?? "_invalid_",
    role,
    onMessage: handleMessage,
  });

  const onSignal = useCallback((handler: (msg: ServerMsg) => void) => {
    signalHandlerRef.current = handler;
  }, []);

  const bothPresent = !!(state?.trainer.connected && state?.case.connected);
  const {
    localStream, remoteStream, screenStream, mediaError, peerError, setScreenTrack,
    videoDevices, audioDevices, videoDeviceId, audioDeviceId,
    setVideoDeviceId, setAudioDeviceId,
  } = useStudioMedia({ role, bothPresent, send, onSignal });

  if (!sessionId || !user) {
    return <main className="page page--auth"><div className="muted">…</div></main>;
  }

  if (!connected || !state) {
    return (
      <main className="page page--auth">
        <div className="serial">Studio · 連線中…</div>
        {error && <div className="alert" style={{ marginTop: "var(--s-4)" }}>{error}</div>}
      </main>
    );
  }

  if (state.phase === "ended") {
    return (
      <main className="page page--auth">
        <div className="serial">SESSION · ended</div>
        <h1>Session 已結束</h1>
        <p className="muted" style={{ marginTop: "var(--s-3)" }}>
          {state.endedAt && `結束於 ${state.endedAt}`}
        </p>
        <Link to={role === "case" ? `/me/sessions/${sessionId}` : `/dashboard/sessions/${sessionId}`}
              className="btn btn--ghost" style={{ marginTop: "var(--s-5)" }}>
          看 session 紀錄 →
        </Link>
      </main>
    );
  }

  return state.phase === "lobby"
    ? <Lobby role={role} state={state} send={send} sessionId={sessionId}
             localStream={localStream} remoteStream={remoteStream}
             mediaError={mediaError} peerError={peerError}
             videoDevices={videoDevices} audioDevices={audioDevices}
             videoDeviceId={videoDeviceId} audioDeviceId={audioDeviceId}
             setVideoDeviceId={setVideoDeviceId} setAudioDeviceId={setAudioDeviceId} />
    : <StudioLive role={role} state={state} send={send} sessionId={sessionId}
                  localStream={localStream} remoteStream={remoteStream}
                  screenStream={screenStream} setScreenTrack={setScreenTrack}
                  peerError={peerError} metrics={metrics}
                  caseSubappState={caseSubappState}
                  rawEegRef={rawEegRef} rawEegSps={rawEegSps} rawEegTs={rawEegTs} />;
}

/* ═══════════════════════════════════════════════════════════════════
   Lobby — pre-session video call + consent + start
   ═══════════════════════════════════════════════════════════════════ */
function Lobby({
  role, state, send, sessionId, localStream, remoteStream, mediaError, peerError,
  videoDevices, audioDevices, videoDeviceId, audioDeviceId,
  setVideoDeviceId, setAudioDeviceId,
}: {
  role: Role;
  state: RoomState;
  send: (msg: any) => void;
  sessionId: string;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  mediaError: string | null;
  peerError: string | null;
  videoDevices: { deviceId: string; label: string }[];
  audioDevices: { deviceId: string; label: string }[];
  videoDeviceId: string | null;
  audioDeviceId: string | null;
  setVideoDeviceId: (id: string | null) => void;
  setAudioDeviceId: (id: string | null) => void;
}) {
  const peerLabel = role === "trainer" ? "個案" : "訓練師";
  const peerConnected = role === "trainer" ? state.case.connected : state.trainer.connected;

  // Consent satisfaction logic: trainer needs trainer_recording;
  // case needs both case_recording + case_no_medical
  const trainerOk = state.consent.trainer_recording;
  const caseOk    = state.consent.case_recording && state.consent.case_no_medical;
  const allConsentsOk = trainerOk && caseOk;
  const myConsentsOk  = role === "trainer" ? trainerOk : caseOk;

  return (
    <div style={{ display: "grid", gridTemplateRows: "auto 1fr", height: "100vh", overflow: "hidden" }}>
      <header className="mast" style={{ padding: "10px var(--s-5)" }}>
        <div className="row" style={{ gap: "var(--s-5)" }}>
          <div className="mast__brand" style={{ fontSize: "var(--t-base)" }}>
            <span>CogniHome</span>
            <span className="slash">/</span>
            <span style={{ color: "var(--ink-soft)" }}>Lobby · 等候開始</span>
          </div>
          <span className="serial">SID {sessionId.slice(0, 12)}</span>
          <span className={peerConnected ? "chip chip--olive" : "chip chip--warning"}>
            {peerConnected ? `● ${peerLabel} 已連線` : `● 等候 ${peerLabel}…`}
          </span>
        </div>
        <nav className="mast__nav">
          <Link to={role === "case" ? "/me" : "/dashboard"} style={{ fontSize: "var(--t-sm)" }}>離開</Link>
        </nav>
      </header>

      <main style={{ padding: "var(--s-5) var(--s-6)", overflow: "auto" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "var(--s-5)",
          maxWidth: 1100,
          margin: "0 auto",
        }}>
          {/* My video */}
          <VideoTile label={`你 (${role === "trainer" ? "訓練師" : "個案"})`}
                     stream={localStream} mirror local
                     fallback={mediaError ?? "等候攝影機…"} />

          {/* Peer video */}
          <VideoTile label={peerLabel}
                     stream={remoteStream} mirror={false}
                     fallback={peerConnected ? "等候 video stream…" : `${peerLabel}尚未進入 lobby`} />
        </div>

        {/* P8-1: device picker — let user switch between cameras / mics
            without leaving lobby. Selection persists in localStorage so
            same device is reused next session. Mid-session swap uses
            sender.replaceTrack (no SDP renegotiation). */}
        <div style={{
          maxWidth: 1100, margin: "var(--s-4) auto 0",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-4)",
        }}>
          <DevicePicker
            label="📹 攝影機"
            devices={videoDevices}
            selected={videoDeviceId}
            onSelect={setVideoDeviceId}
            placeholder="(預設裝置)"
          />
          <DevicePicker
            label="🎤 麥克風"
            devices={audioDevices}
            selected={audioDeviceId}
            onSelect={setAudioDeviceId}
            placeholder="(預設裝置)"
          />
        </div>

        {peerError && (
          <div className="alert" style={{ maxWidth: 1100, margin: "var(--s-4) auto" }}>
            {peerError}
          </div>
        )}

        <section className="surface rise rise-2" style={{
          maxWidth: 720, margin: "var(--s-6) auto 0",
          padding: "var(--s-6) var(--s-7)",
        }}>
          <div className="serial" style={{ marginBottom: "var(--s-3)" }}>同意確認</div>
          <h2 style={{ fontSize: "var(--t-xl)", marginBottom: "var(--s-4)" }}>
            開始前<span className="italic-display" style={{ color: "var(--rust)" }}>必填</span>
          </h2>

          {role === "trainer" ? (
            <ConsentItem
              checked={state.consent.trainer_recording}
              onCheck={() => send({ type: "consent", kind: "recording" })}
              text="我同意這次 session 進行錄製（EEG / 行為 / 視訊存留以供事後回顧）"
            />
          ) : (
            <>
              <ConsentItem
                checked={state.consent.case_recording}
                onCheck={() => send({ type: "consent", kind: "recording" })}
                text="我同意這次 session 進行錄製（EEG / 行為 / 視訊存留以供事後回顧）"
              />
              <div style={{ height: "var(--s-3)" }} />
              <ConsentItem
                checked={state.consent.case_no_medical}
                onCheck={() => send({ type: "consent", kind: "no_medical_claim" })}
                text="我了解 CogniHome 不是醫療診斷或治療系統"
              />
            </>
          )}

          <div className="rule" />

          <div className="row row--between">
            <div style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)" }}>
              {role === "trainer" ? (
                <>訓練師 {trainerOk ? "✓" : "○"} · 個案 {caseOk ? "✓" : "○"}</>
              ) : (
                <>{myConsentsOk ? "你已同意。等訓練師開始" : "請完成上方兩項同意"}</>
              )}
            </div>

            {role === "trainer" ? (
              <button
                className="btn btn--rust"
                disabled={!allConsentsOk || !state.case.connected}
                onClick={() => send({ type: "start_session" })}
              >
                {!state.case.connected ? "等候個案連線"
                : !allConsentsOk        ? "等所有同意完成"
                                        : "開始 Session →"}
              </button>
            ) : (
              <span className="muted" style={{ fontSize: "var(--t-sm)" }}>
                訓練師會在這之後按開始
              </span>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function ConsentItem({ checked, onCheck, text }: { checked: boolean; onCheck: () => void; text: string }) {
  return (
    <button onClick={onCheck} disabled={checked} style={{
      display: "flex", alignItems: "flex-start", gap: "var(--s-3)",
      width: "100%", textAlign: "left", padding: "var(--s-3) var(--s-4)",
      background: checked ? "var(--olive-soft)" : "var(--paper)",
      border: `1px solid ${checked ? "var(--olive)" : "var(--hairline)"}`,
      borderRadius: "var(--r-sm)",
      cursor: checked ? "default" : "pointer",
      fontFamily: "var(--body)",
      transition: "all 160ms",
    }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 4,
        border: `1.5px solid ${checked ? "var(--olive)" : "var(--ink-faint)"}`,
        background: checked ? "var(--olive)" : "var(--paper)",
        color: "var(--paper)", fontSize: 14, flexShrink: 0,
      }}>
        {checked ? "✓" : ""}
      </span>
      <span style={{ flex: 1, fontSize: "var(--t-sm)", lineHeight: 1.55, color: checked ? "var(--olive)" : "var(--ink)" }}>
        {text}
      </span>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   StudioLive — in-session real WS-wired (placeholder content per P3)
   ═══════════════════════════════════════════════════════════════════ */
const STUDIO_TABS = [
  { id: "video",   num: "01", label: "影像 ＆ Yui", en: "Video"   },
  { id: "signals", num: "02", label: "訊號",        en: "Signals" },
  { id: "log",     num: "03", label: "紀錄",        en: "Log"     },
] as const;
type StudioTabId = typeof STUDIO_TABS[number]["id"];

function StudioLive({ role, state, send, sessionId, localStream, remoteStream, screenStream, setScreenTrack, peerError, metrics, caseSubappState, rawEegRef, rawEegSps, rawEegTs }: {
  role: Role;
  state: RoomState;
  send: (msg: any) => void;
  sessionId: string;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  screenStream: MediaStream | null;
  setScreenTrack: (track: MediaStreamTrack | null) => Promise<void>;
  peerError: string | null;
  metrics: Record<string, { value: number; ts: number }>;
  caseSubappState: string | null;
  rawEegRef: React.MutableRefObject<Record<string, number[]>>;
  rawEegSps: number;
  rawEegTs:  number;
}) {
  const elapsed = useElapsed(state.startedAt);
  const [tab, setTab] = useState<StudioTabId>("video");

  // Case sees a minimal interface (sub-app iframe + small trainer cam)
  if (role === "case") {
    return <CaseSubjectView trainerStream={remoteStream} localStream={localStream}
                            setScreenTrack={setScreenTrack}
                            state={state} send={send} sessionId={sessionId} />;
  }

  // Trainer view — mirrors /demo (DesignDemo.StudioDemo) layout exactly:
  // grid rows = mast / tab-strip / content, cols = 1fr + 420px right rail.
  return (
    <div style={{
      display: "grid",
      gridTemplateRows: "auto auto 1fr",
      gridTemplateColumns: "1fr 420px",
      height: "100vh",
      overflow: "hidden",
    }}>
      {/* ── compact mast (full width) ── */}
      <header className="mast" style={{
        gridColumn: "1 / 3",
        padding: "10px var(--s-5)",
        borderBottom: "1px solid var(--ink)",
      }}>
        <div className="row" style={{ gap: "var(--s-5)" }}>
          <div className="mast__brand" style={{ fontSize: "var(--t-base)" }}>
            <span>CogniHome</span>
            <span className="slash">/</span>
            <span style={{ color: "var(--ink-soft)" }}>Studio</span>
          </div>
          <span className={state.phase === "live" ? "chip chip--olive" : state.phase === "paused" ? "chip chip--warning" : "chip"}>
            {state.phase === "live" ? "live" : state.phase === "paused" ? "paused" : state.phase} · {elapsed}
          </span>
          <span className="serial">SID {sessionId.slice(0, 12)}</span>
        </div>
        <nav className="mast__nav">
          <span className="muted" style={{ fontSize: "var(--t-sm)" }}>
            個案 <span className="italic-display" style={{ color: "var(--ink)" }}>{state.case.connected ? "已連線" : "未連線"}</span>
          </span>
          <span style={{ color: "var(--ink-ghost)" }}>·</span>
          <a href="#" onClick={(e) => {
            e.preventDefault();
            if (confirm("結束此 session 並儲存？")) send({ type: "end_session" });
          }} style={{ color: "var(--rust)" }}>結束 session</a>
        </nav>
      </header>

      {/* ── tab strip ── */}
      <nav style={{
        gridColumn: "1 / 2",
        display: "flex",
        borderBottom: "1px solid var(--hairline)",
        padding: "0 var(--s-5)",
        background: "var(--paper)",
      }}>
        {STUDIO_TABS.map(t => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: "transparent", border: "none",
              padding: "14px 18px 12px", cursor: "pointer", position: "relative",
              display: "flex", alignItems: "baseline", gap: 8,
              color: active ? "var(--ink)" : "var(--ink-soft)", transition: "color 160ms",
            }}>
              <span className="serial" style={{ color: active ? "var(--rust)" : "var(--ink-faint)" }}>{t.num}</span>
              <span className="italic-display" style={{
                fontSize: "var(--t-md)", fontStyle: "italic",
                fontWeight: active ? 500 : 400,
              }}>{t.label}</span>
              <span className="muted" style={{ fontSize: "var(--t-xs)", fontFamily: "var(--mono)", marginLeft: 4 }}>{t.en}</span>
              {active && <span style={{ position: "absolute", bottom: -1, left: 0, right: 0, height: 2, background: "var(--ink)" }} />}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
      </nav>

      {/* ── tab content ── */}
      <section style={{
        gridColumn: "1 / 2",
        padding: "var(--s-5)",
        overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {tab === "video"   && <VideoTabLive state={state} metrics={metrics} remoteStream={remoteStream} screenStream={screenStream} sessionId={sessionId} peerError={peerError} />}
        {tab === "signals" && <SignalsTabLive metrics={metrics} rawEegRef={rawEegRef} rawEegSps={rawEegSps} rawEegTs={rawEegTs} />}
        {tab === "log"     && <LogTabLive sessionId={sessionId} />}
      </section>

      {/* ── right rail · pure parameters + apps · no scroll ── */}
      <aside style={{
        gridColumn: "2 / 3",
        gridRow: "2 / 4",
        background: "var(--paper-warm)",
        borderLeft: "1px solid var(--hairline)",
        padding: "var(--s-4) var(--s-5)",
        overflow: "hidden",
        display: "flex", flexDirection: "column",
        gap: "var(--s-4)",
      }}>
        <RightRailLive state={state} send={send} caseSubappState={caseSubappState} metrics={metrics} />
      </aside>

      {/* Hidden local video element for keep-alive */}
      <HiddenLocalVideo stream={localStream} />
    </div>
  );
}

/* ─── TAB 1: 影像 ＆ Yui ─── */
function VideoTabLive({ state, metrics, remoteStream, screenStream, sessionId, peerError }: {
  state: RoomState;
  metrics: Record<string, { value: number; ts: number }>;
  remoteStream: MediaStream | null;
  screenStream: MediaStream | null;
  sessionId: string;
  peerError: string | null;
}) {
  return (
    <div className="col" style={{ flex: 1, gap: "var(--s-4)", minHeight: 0 }}>
      {/* Top row: 個案攝影機 / 個案螢幕 / Reward panel */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--s-4)" }}>
        <div>
          <div className="serial" style={{ marginBottom: 4 }}>個案攝影機 ● live</div>
          <VideoBox stream={remoteStream} fallback="等候 stream…" />
          {peerError && <div className="alert" style={{ marginTop: 6, fontSize: "var(--t-xs)" }}>{peerError}</div>}
        </div>
        <div>
          <div className="serial" style={{ marginBottom: 4 }}>個案螢幕鏡像 ● {screenStream ? "live" : "等待"}</div>
          <VideoBox stream={screenStream} fallback={state.activeTask
            ? "等候個案遊戲畫面…"
            : state.game ? `${state.game} · 等候鏡像 stream` : "等候 sub-app"} />
        </div>
        <RewardPanelLive state={state} metrics={metrics} />
      </div>

      {/* Bottom row: 行為觀察 / Yui */}
      <div style={{ flex: 1, minHeight: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--s-4)" }}>
        <div className="surface" style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
            <div>
              <div className="serial">行為觀察 · markers</div>
              <div style={{ fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>手動標記要對比訊號的時刻</div>
            </div>
            <span className="chip">P6 待接</span>
          </div>
          <div className="muted" style={{ fontSize: "var(--t-xs)", flex: 1 }}>
            手動標記功能在 P6 加入（spec §13）。session 中的系統事件可在「紀錄」tab 看完整 timeline。
          </div>
        </div>

        <div className="surface" style={{ padding: "var(--s-4)", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
            <div>
              <div className="serial">Yui · advisory</div>
              <div style={{ fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>純諮詢 · 不寫參數</div>
            </div>
            <span className="chip">P6 待接</span>
          </div>
          <div className="muted" style={{ fontSize: "var(--t-xs)", flex: 1 }}>
            Yui advisor 將在 P6 接入（streaming session state → yui-core 即時回覆）。
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── TAB 2: 訊號 — sgimacog impedance + raw waveform + band power ─── */
function SignalsTabLive({ metrics, rawEegRef, rawEegSps, rawEegTs }: {
  metrics: Record<string, { value: number; ts: number }>;
  rawEegRef: React.MutableRefObject<Record<string, number[]>>;
  rawEegSps: number;
  rawEegTs:  number;
}) {
  // Channel order matches cognihome-soramynd's CHANNEL_LABELS — soramynd's
  // 8-ch Steeg headset is the only EEG source CogniHome currently
  // supports. Was hard-coded as 10-20 standard (Fp1/2 F3/4 C3/4 P3 Pz)
  // which silently mismatched the device, leaving 5/8 cells "—" forever.
  const channels = ["Fp1", "Fp2", "T7", "T8", "O1", "O2", "Fz", "Pz"];
  return (
    <div className="col" style={{ flex: 1, gap: "var(--s-4)", minHeight: 0 }}>
      <div className="surface" style={{ padding: "var(--s-4) var(--s-5)" }}>
        <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
          <div>
            <div className="serial">Impedance · 8ch · target &lt; 10 kΩ</div>
            <div style={{ fontSize: "var(--t-sm)", color: "var(--ink-soft)", marginTop: 4 }}>
              {Object.keys(metrics).filter(k => k.startsWith("impedance")).length === 0
                ? "等候 sgimacog impedance 訊號 (case 端 sub-app 連上後自動更新)"
                : "live"}
            </div>
          </div>
          <span className="chip">{Object.keys(metrics).filter(k => k.startsWith("impedance")).length} active</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: "var(--s-2)" }}>
          {channels.map(ch => {
            const m = metrics[`impedance.${ch}`];
            const kΩ = m ? m.value : null;
            const ok = kΩ !== null && kΩ < 10;
            return (
              <div key={ch} style={{
                padding: "8px 4px",
                background: kΩ === null ? "var(--paper-deep)" : ok ? "rgba(94, 116, 38, 0.18)" : "rgba(168, 51, 35, 0.35)",
                border: `1px solid ${kΩ === null ? "var(--hairline)" : ok ? "#3a4f1c" : "var(--danger)"}`,
                borderRadius: 2, textAlign: "center",
              }}>
                <div style={{ fontFamily: "var(--mono)", fontSize: 10, color: "var(--ink-soft)" }}>{ch}</div>
                <div style={{ fontFamily: "var(--mono)", fontSize: 12, fontWeight: 600 }}>
                  {kΩ === null ? "—" : `${kΩ.toFixed(1)} kΩ`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <RawEegPanel rawEegRef={rawEegRef} rawEegSps={rawEegSps} rawEegTs={rawEegTs} channels={channels} />

      <BandPowerPanel metrics={metrics} channels={channels} />
    </div>
  );
}

/**
 * RawEegPanel — scrolling 8-channel waveform from BC `raw_preview`.
 * Wired 2026-05-09 once cognihome-soramynd's eegBridge started
 * forwarding samples on tr-{room} BC. Re-paints at 30 fps via
 * requestAnimationFrame; reads the live ring buffer mutated by Studio's
 * top-level BC subscription.
 */
function RawEegPanel({ rawEegRef, rawEegSps, rawEegTs, channels }: {
  rawEegRef: React.MutableRefObject<Record<string, number[]>>;
  rawEegSps: number;
  rawEegTs:  number;
  channels:  string[];
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fresh = rawEegTs > 0 && (Date.now() - rawEegTs) < 2000;

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const dpr = window.devicePixelRatio || 1;
    let raf = 0;
    let alive = true;

    const resize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      canvas.width  = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      canvas.style.width  = `${w}px`;
      canvas.style.height = `${h}px`;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const draw = () => {
      if (!alive) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) { raf = requestAnimationFrame(draw); return; }
      const w = canvas.width, h = canvas.height;
      ctx.fillStyle = "#0d0a07";
      ctx.fillRect(0, 0, w, h);

      const rowH = h / channels.length;
      ctx.font = `${10 * dpr}px ui-monospace, monospace`;
      ctx.textBaseline = "top";

      for (let i = 0; i < channels.length; i++) {
        const ch = channels[i]!;
        const trace = rawEegRef.current[ch];
        const yMid = rowH * (i + 0.5);
        // Row separator
        ctx.strokeStyle = "rgba(255,255,255,0.08)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, rowH * i + 0.5);
        ctx.lineTo(w, rowH * i + 0.5);
        ctx.stroke();
        // Label
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.fillText(ch, 6 * dpr, rowH * i + 4 * dpr);

        if (!trace || trace.length < 2) continue;

        // Auto-scale per channel using p99 absolute value (so a single
        // saturation spike doesn't squash the rest of the trace).
        let p99 = 1;
        const sample = trace.length > 200 ? trace.slice(-200) : trace;
        const sorted = sample.map(Math.abs).sort((a, b) => a - b);
        p99 = sorted[Math.floor(sorted.length * 0.99)] ?? 1;
        if (!Number.isFinite(p99) || p99 < 1) p99 = 50;
        const scale = (rowH * 0.4) / p99;

        ctx.strokeStyle = "#7fc8e8";
        ctx.lineWidth = 1.5 * dpr;
        ctx.beginPath();
        const N = trace.length;
        for (let j = 0; j < N; j++) {
          const x = (j / (N - 1)) * w;
          const y = yMid - (trace[j]! * scale);
          if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [rawEegRef, channels]);

  return (
    <div className="surface" style={{ padding: "var(--s-4) var(--s-5)", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div className="row row--between" style={{ marginBottom: "var(--s-3)" }}>
        <div className="serial">Raw EEG · 8ch · {rawEegSps} SPS preview</div>
        <span style={{
          fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
          color: rawEegTs === 0 ? "var(--ink-faint)" : fresh ? "var(--olive)" : "var(--warning)",
        }}>
          {rawEegTs === 0 ? "● 等候訊號…" : fresh ? "● live" : "● stale"}
        </span>
      </div>
      <div ref={containerRef} style={{
        flex: 1, minHeight: 240,
        background: "#0d0a07", borderRadius: 2,
        position: "relative",
      }}>
        <canvas ref={canvasRef} style={{ display: "block" }} />
      </div>
    </div>
  );
}

/**
 * BandPowerPanel — table of mean / per-channel band power values from
 * cognihome-soramynd's eegBridge. Reads `eeg.<band>.<channel>` and
 * `eeg.<band>.mean` metric keys produced every 500 ms.
 */
function BandPowerPanel({ metrics, channels }: {
  metrics: Record<string, { value: number; ts: number }>;
  channels: string[];
}) {
  const bands = [
    { key: "delta", label: "δ Delta",  hint: "0.5–4 Hz" },
    { key: "theta", label: "θ Theta",  hint: "4–8 Hz"   },
    { key: "alpha", label: "α Alpha",  hint: "8–13 Hz"  },
    { key: "smr",   label: "SMR",      hint: "12–15 Hz" },
    { key: "beta",  label: "β Beta",   hint: "13–30 Hz" },
  ];
  const fmt = (v?: number) => v === undefined || !Number.isFinite(v)
    ? "—" : v < 1 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : v.toFixed(0);

  return (
    <div className="surface" style={{ padding: "var(--s-4) var(--s-5)" }}>
      <div className="serial" style={{ marginBottom: "var(--s-3)" }}>
        Band power · µV² · 500 ms cadence
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "auto repeat(" + channels.length + ", 1fr) auto",
        fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        gap: 1, background: "var(--hairline)",
      }}>
        <div style={{ background: "var(--paper-warm)", padding: "4px 6px", color: "var(--ink-soft)" }}>band</div>
        {channels.map(ch => (
          <div key={ch} style={{ background: "var(--paper-warm)", padding: "4px 6px", textAlign: "center", color: "var(--ink-soft)" }}>{ch}</div>
        ))}
        <div style={{ background: "var(--paper-warm)", padding: "4px 6px", textAlign: "center", color: "var(--ink-soft)" }}>mean</div>

        {bands.map(b => (
          <React.Fragment key={b.key}>
            <div style={{ background: "var(--paper)", padding: "4px 6px" }}>
              {b.label}
              <span style={{ color: "var(--ink-faint)", marginLeft: 4, fontSize: 10 }}>{b.hint}</span>
            </div>
            {channels.map(ch => {
              const v = metrics[`eeg.${b.key}.${ch}`]?.value;
              return (
                <div key={ch} style={{ background: "var(--paper)", padding: "4px 6px", textAlign: "center" }}>
                  {fmt(v)}
                </div>
              );
            })}
            <div style={{ background: "var(--paper)", padding: "4px 6px", textAlign: "center", fontWeight: 600 }}>
              {fmt(metrics[`eeg.${b.key}.mean`]?.value)}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* ─── TAB 3: 紀錄 — real session_events from /api/sessions/:id/events ─── */
function LogTabLive({ sessionId }: { sessionId: string }) {
  const [events, setEvents] = useState<Array<{ id: number; ts: string; actor: "trainer"|"case"|"system"; type: string; payload: string | null }>>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await api<{ ok: true; events: typeof events }>(`/api/sessions/${sessionId}/events`);
      setEvents(r.events);
    } finally { setLoading(false); }
  }
  useEffect(() => {
    load();
    const id = window.setInterval(load, 5000);
    return () => window.clearInterval(id);
  }, [sessionId]);

  return (
    <div className="col" style={{ flex: 1, gap: "var(--s-4)", minHeight: 0 }}>
      <div className="row row--between">
        <div className="serial">SESSION EVENTS · {events.length}{loading && events.length === 0 ? " · 載入中" : ""}</div>
        <button className="btn btn--small btn--ghost" onClick={() => exportEventsCsv(events, sessionId)} disabled={events.length === 0}>
          匯出 CSV
        </button>
      </div>
      <div className="surface" style={{ flex: 1, minHeight: 0, padding: 0, overflow: "hidden" }}>
        <div style={{ height: "100%", overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--t-sm)" }}>
            <thead>
              <tr style={{ background: "var(--paper-warm)", position: "sticky", top: 0, zIndex: 1 }}>
                <th style={evTh}>時間</th>
                <th style={evTh}>來源</th>
                <th style={evTh}>事件</th>
                <th style={evTh}>內容</th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => (
                <tr key={ev.id} style={{ borderTop: "1px solid var(--hairline)" }}>
                  <td style={{ ...evTd, fontFamily: "var(--mono)", fontSize: "var(--t-xs)", whiteSpace: "nowrap" }}>{ev.ts.slice(11, 19)}</td>
                  <td style={evTd}>
                    {ev.actor === "trainer" && <span className="chip chip--rust" style={{ fontSize: "var(--t-xs)" }}>訓練師</span>}
                    {ev.actor === "case"    && <span className="chip chip--olive" style={{ fontSize: "var(--t-xs)" }}>個案</span>}
                    {ev.actor === "system"  && <span className="chip" style={{ fontSize: "var(--t-xs)" }}>系統</span>}
                  </td>
                  <td style={{ ...evTd, fontFamily: "var(--mono)", fontSize: "var(--t-xs)" }}>{ev.type}</td>
                  <td style={{ ...evTd, fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>
                    {ev.payload && ev.payload.length > 60 ? ev.payload.slice(0, 60) + "…" : ev.payload ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const evTh: CSSProperties = {
  textAlign: "left", padding: "var(--s-2) var(--s-3)",
  fontSize: "var(--t-xs)", letterSpacing: "0.08em",
  color: "var(--ink-soft)", fontWeight: 500, textTransform: "uppercase",
};
const evTd: CSSProperties = { padding: "var(--s-2) var(--s-3)", verticalAlign: "top" };

function exportEventsCsv(events: Array<{ id: number; ts: string; actor: string; type: string; payload: string | null }>, sid: string) {
  const rows = [["id","ts","actor","type","payload"], ...events.map(e => [String(e.id), e.ts, e.actor, e.type, e.payload ?? ""])];
  const csv = rows.map(r => r.map(v => /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `cognihome-events-${sid}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* Case-side subject view: sub-app iframe + trainer thumbnail + BC↔WS bridge */
function CaseSubjectView({ trainerStream, localStream, setScreenTrack, state, send, sessionId }: {
  trainerStream: MediaStream | null;
  localStream: MediaStream | null;
  setScreenTrack: (track: MediaStreamTrack | null) => Promise<void>;
  state: RoomState;
  send: (msg: any) => void;
  sessionId: string;
}) {
  // Session recording — case-side webcam+mic chunked to R2 while
  // phase === live. The hook reads the access token fresh from
  // sessionStorage on every chunk upload so a 2 h JWT refresh
  // mid-session doesn't 401 the recorder out.
  const rec = useRecorder({
    stream:  localStream,
    sessionId,
    active:  state.phase === "live",
  });
  // ─── BroadcastChannel ↔ WS bridge ────────────────────────────────
  // Case parent owns the local BC; sub-app opens (iframe / window) on
  // same origin and joins the same channel. We forward sub-app emits
  // (metric/impedance/state/event) → trainer via DO WS, and forward
  // trainer-pushed control messages (params/source/game/start/pause/stop)
  // from WS → BC for sub-app consumption.
  const bcRef = useRef<BroadcastChannel | null>(null);

  // Raw EEG capture buffer — accumulates raw_preview chunks BC delivers
  // and POSTs to R2 in 5 s windows. Dormant until a sub-app emits;
  // mock sub-apps don't, so this is no-op for current testing.
  const rawBufRef    = useRef<{ samples: number[]; channels: string[]; sps: number; startedAt: string | null }>(
    { samples: [], channels: [], sps: 0, startedAt: null }
  );
  const rawSeqRef    = useRef(0);
  const rawFlushRef  = useRef(0);

  // Track which params we've pushed to the current iframe so we only
  // re-emit on actual changes (M1 fix: start from empty so the first
  // push covers the initial state, not just deltas after first render).
  const lastParams       = useRef<Record<string, number | string | boolean>>({});
  // Stable ref for the current room state — replay-on-ready needs to
  // read the latest state without re-subscribing the BC channel.
  const stateRef         = useRef(state);
  stateRef.current       = state;
  const failedRawSeqs    = useRef<number[]>([]);
  const [rawHealth, setRawHealth] = useState<{ uploaded: number; failed: number }>({ uploaded: 0, failed: 0 });

  // Allowlist of acceptable iframe origins for postMessage. After P7
  // proxy refactor (2026-05-09), forks are served same-origin via
  // cognihome-web Pages Functions at /sub/<fork>/*, so the iframe's
  // window.location.origin matches our own page. BroadcastChannel
  // `tr-{room}` works directly across same-origin iframe ↔ parent —
  // postMessage path is now defensive belt-and-braces, not strictly
  // required, but kept for compatibility with the forks' existing
  // dual-transport bridge code.
  const COGNI_SUBAPP_ORIGINS = new Set([
    window.location.origin,
    // Legacy direct .pages.dev URLs — kept in case of mid-rollout iframe
    // links from cached UIs; safe to keep allowlisted since the forks
    // are still reachable cross-origin.
    "https://cognihome-rppg.pages.dev",
    "https://cognihome-rppg-staging.pages.dev",
    "https://cognihome-myndscape.pages.dev",
    "https://cognihome-myndscape-staging.pages.dev",
    "https://cognihome-soramynd.pages.dev",
    "https://cognihome-soramynd-staging.pages.dev",
  ]);

  useEffect(() => {
    const bc = new BroadcastChannel(`tr-${sessionId}`);
    bcRef.current = bc;

    // Handler shared between BC (same-origin mocks) and window 'message'
    // (cross-origin sub-app forks). `bcOut` is how we push trainer-side
    // control messages back to the sub-app — for BC subscribers this is
    // the BC instance; for cross-origin iframes the caller passes an
    // adapter that postMessages to iframe.contentWindow.
    function handleSubappMessage(
      m: any,
      bcOut: { postMessage: (msg: any) => void },
    ) {
      // ── H1 fix: replay full state when sub-app announces ready.
      // Mid-session source/game switches re-load the iframe; without a
      // replay the new sub-app boots into defaults because params /
      // source / game / phase were broadcast before it subscribed.
      if (m.type === "subapp_state" && m.state === "ready") {
        const s = stateRef.current;
        if (s.source !== null) bcOut.postMessage({ type: "set_source", source: s.source });
        if (s.game   !== null) bcOut.postMessage({ type: "set_game",   game:   s.game });
        for (const [k, v] of Object.entries(s.params)) {
          bcOut.postMessage({ type: "params_update", key: k, value: v });
        }
        bcOut.postMessage({
          type: s.phase === "live" ? "start" :
                s.phase === "paused" ? "pause" : "stop",
        });
        // Replay armed task too — a sub-app reload mid-armed needs to know.
        if (s.activeTask && (s.taskState === "armed" || s.taskState === "running")) {
          bcOut.postMessage({
            type: "arm_task",
            task: s.activeTask,
            params: s.taskParams ?? {},
          });
          if (s.taskState === "running") bcOut.postMessage({ type: "start_task" });
        }
        // Forward to trainer too (audit + UI freshness chip)
        send({ type: "subapp_state", state: "ready", source: m.source, task: m.task });
        return;
      }

      // H2: forward sub-app lifecycle / events to trainer + audit log
      if (m.type === "subapp_state") {
        send({ type: "subapp_state", state: m.state, source: m.source, task: m.task });
        return;
      }
      if (m.type === "subapp_event") {
        send({ type: "subapp_event", kind: m.kind, payload: m.payload });
        return;
      }

      // Forward case-side metrics to trainer via DO WS. demo flag is
      // preserved end-to-end so metric_snapshots.demo column matches.
      if (m.type === "metric") {
        send({ type: "metric", source: m.source, value: m.value, ts: m.ts, demo: m.demo === true });
        return;
      }

      // task_complete (sub-app reports task finished) → forward to DO,
      // which updates taskState=completed and writes session_events.
      if (m.type === "task_complete") {
        send({ type: "task_complete", task: m.task, results: m.results });
        return;
      }

      // Raw EEG preview: forward to trainer over WS for the scrolling
      // canvas. BC doesn't cross browsers (trainer + case typically on
      // different machines), so the WS hop is mandatory for live
      // monitoring. R2 chunked upload for the recording uses the
      // dedicated /api/sessions/:id/raw-eeg/chunk endpoint (driven
      // separately by flushRawEegWithRetry on phase=ended), so we don't
      // double-buffer here.
      if (m.type === "raw_preview" && Array.isArray(m.samples)) {
        send({
          type: "raw_preview",
          channels: Array.isArray(m.channels) ? m.channels : [],
          sps: typeof m.sps === "number" ? m.sps : 100,
          samples: m.samples as number[][],
          ts: typeof m.ts === "number" ? m.ts : Date.now(),
        });
        // Legacy R2 buffer path (kept for compatibility but currently
        // dormant — eegBridge ships nested [ch][n] which the old flat
        // accumulator can't index correctly. Will rebuild as a proper
        // per-channel R2 manifest in the next pass.)
        const buf = rawBufRef.current;
        if (!buf.startedAt) buf.startedAt = new Date().toISOString();
        if (buf.channels.length === 0 && Array.isArray(m.channels)) buf.channels = m.channels;
        if (!buf.sps && typeof m.sps === "number") buf.sps = m.sps;
        const now = Date.now();
        if (now - rawFlushRef.current > 5000 || buf.samples.length * 4 > 100_000) {
          rawFlushRef.current = now;
          const seq = rawSeqRef.current;
          rawSeqRef.current += 1;
          const samples = buf.samples;
          buf.samples = [];
          flushRawEegWithRetry(sessionId, seq, samples).then(ok => {
            setRawHealth(h => ok
              ? { ...h, uploaded: h.uploaded + 1 }
              : { ...h, failed: h.failed + 1 });
            if (!ok) failedRawSeqs.current.push(seq);
          });
        }
      }
    }

    // Path 1: same-origin sub-apps via BroadcastChannel
    bc.onmessage = (ev) => {
      console.log("[BC parent←]", ev.data?.type, ev.data?.source ?? "", ev.data?.state ?? "");
      handleSubappMessage(ev.data, bc);
    };

    // Path 2: cross-origin sub-app forks via postMessage. Origin filter is
    // critical — `*` listener without one would accept events from any
    // page that can window.open us. Outbound replies use postMessage to
    // iframe.contentWindow so the fork receives them.
    function onWindowMessage(ev: MessageEvent) {
      if (!COGNI_SUBAPP_ORIGINS.has(ev.origin)) {
        // Helpful when a fork's origin is missing from the allowlist —
        // otherwise the silent reject is impossible to diagnose remotely.
        if (ev.data && typeof ev.data === "object" && (ev.data as { type?: string }).type) {
          console.debug("[Telerehab postMessage rejected origin]", ev.origin);
        }
        return;
      }
      console.debug("[Telerehab postMessage←]", ev.origin, (ev.data as { type?: string })?.type);
      const iframe = iframeRef.current;
      const adapter = {
        postMessage: (msg: any) => {
          try { iframe?.contentWindow?.postMessage(msg, ev.origin); } catch { /* */ }
        },
      };
      handleSubappMessage(ev.data, adapter);
    }
    window.addEventListener("message", onWindowMessage);

    return () => {
      bc.close();
      window.removeEventListener("message", onWindowMessage);
    };
  }, [sessionId, send]);

  // On session end, finalize raw EEG manifest if anything was buffered.
  // H3 fix: if any earlier chunks reported failure we surface a UI
  // warning rather than silently posting a finalize that the server
  // will reject 424 with missing_seqs.
  useEffect(() => {
    if (state.phase !== "ended" && state.phase !== "live" && state.phase !== "paused") return;
    if (state.phase === "ended" && rawSeqRef.current > 0) {
      const buf = rawBufRef.current;
      const tail = buf.samples;
      buf.samples = [];
      const finalSeq = rawSeqRef.current;
      (async () => {
        if (tail.length > 0) {
          const ok = await flushRawEegWithRetry(sessionId, finalSeq, tail);
          if (ok) setRawHealth(h => ({ ...h, uploaded: h.uploaded + 1 }));
          else    setRawHealth(h => ({ ...h, failed:   h.failed + 1 }));
          rawSeqRef.current = finalSeq + 1;
        }
        if (failedRawSeqs.current.length > 0) {
          // Don't finalize — server would 424 incomplete_recording.
          // Console warn so the trainer side can decide what to do
          // (manual retry endpoint is a P7 hardening item).
          console.warn(
            "raw-eeg: finalize skipped, %d chunks failed (seqs=%o); recording marked incomplete",
            failedRawSeqs.current.length, failedRawSeqs.current,
          );
          return;
        }
        await finalizeRawEeg(sessionId, rawSeqRef.current, buf.channels, buf.sps, buf.startedAt);
      })().catch((e) => console.error("raw-eeg finalize error", e));
    }
  }, [state.phase, sessionId]);

  // Helper to broadcast a control message both via BC (same-origin mocks)
  // AND via postMessage to the iframe (cross-origin sub-app forks). Mirror
  // semantics: every trainer-driven control change reaches both transports
  // so the sub-app receives it regardless of how it's loaded.
  // 2026-05-09 audit B12: targetOrigin restricted to the iframe's actual
  // origin (parsed from iframe.src). "*" wildcard would let any document
  // navigated into the iframe receive trainer-side messages — including
  // protocol_hint and case_name in future expansions.
  const broadcastToSubapp = useCallback((msg: any) => {
    bcRef.current?.postMessage(msg);
    const iframe = iframeRef.current;
    if (iframe?.contentWindow && iframe.src) {
      try {
        const targetOrigin = new URL(iframe.src).origin;
        iframe.contentWindow.postMessage(msg, targetOrigin);
      } catch { /* invalid src URL */ }
    }
  }, []);

  // When trainer changes params via WS, mirror onto BC for sub-apps.
  // M1 fix: lastParams declared above starts at {} so first-render diff
  // emits all initial params, not zero. Reset to {} when iframe reloads
  // (source/game change) so the freshly-mounted sub-app gets a fresh
  // diff instead of inheriting the prior iframe's seen state.
  useEffect(() => {
    if (!bcRef.current) return;
    for (const [key, value] of Object.entries(state.params)) {
      if (lastParams.current[key] !== value) {
        broadcastToSubapp({ type: "params_update", key, value });
      }
    }
    lastParams.current = { ...state.params };
  }, [state.params, broadcastToSubapp]);

  // Source/game/phase changes → both BC and iframe postMessage
  useEffect(() => {
    if (state.source !== null) broadcastToSubapp({ type: "set_source", source: state.source });
  }, [state.source, broadcastToSubapp]);
  useEffect(() => {
    if (state.game !== null)   broadcastToSubapp({ type: "set_game",   game: state.game });
  }, [state.game, broadcastToSubapp]);
  useEffect(() => {
    broadcastToSubapp({ type: state.phase === "live" ? "start" : state.phase === "paused" ? "pause" : "stop" });
  }, [state.phase, broadcastToSubapp]);

  // ── Armed-vs-running task transitions → forward to sub-app iframe ─────
  // The DO is authoritative for taskState; case parent reacts to state
  // changes and pushes the corresponding control message into the sub-app
  // (BC for same-origin mocks, postMessage for cross-origin forks).
  // arm_task includes task + params so the sub-app knows which task to
  // load; start_task is a bare go signal once the trainer presses 「開始」.
  const lastTaskState = useRef<TaskState>(null);
  useEffect(() => {
    const ts = state.taskState ?? null;
    const prev = lastTaskState.current;
    if (ts === prev) return;
    if (ts === "armed" && state.activeTask) {
      broadcastToSubapp({
        type: "arm_task",
        task: state.activeTask,
        params: state.taskParams ?? {},
      });
    } else if (ts === "running") {
      broadcastToSubapp({ type: "start_task" });
    } else if (ts === null && (prev === "armed" || prev === "running" || prev === "completed")) {
      // Trainer aborted (DO cleared taskState to null) — tell sub-app
      // to reset back to its idle state. Sub-apps already handle `stop`
      // so we reuse it; no new BC message needed.
      broadcastToSubapp({ type: "stop" });
    } else if (ts === "completed") {
      // Case-driven completion (sub-app sent task_complete itself) — also
      // emit stop so the sub-app cleans up; trainer-side TaskControl will
      // show 「裝載下一個 task」 from the completed state UI.
      broadcastToSubapp({ type: "stop" });
    }
    lastTaskState.current = ts;
  }, [state.taskState, state.activeTask, state.taskParams, broadcastToSubapp]);

  // Sub-app embed URL — picks the right sub-app based on trainer's
  // source/game selection. Returns null when nothing's configured yet
  // (so the case sees an explicit "等候訓練師配置" placeholder rather
  // than a stand-in game with synthetic metrics — H4 fix). Switching
  // mid-session re-loads the iframe but BC channel name
  // (tr-{sessionId}) doesn't change, so DO WS pairing + state stays
  // alive across the switch. Reset lastParams when src changes so the
  // newly-mounted sub-app receives the full param set on next push.
  const subappSrc = pickSubapp(
    state.source, state.game, sessionId, state.phase,
    state.activeTask ?? null, state.taskState ?? null,
  );
  useEffect(() => { lastParams.current = {}; }, [subappSrc]);

  // ─── Screen mirror (spec §6.1, line 65: game-canvas via WebRTC) ───
  // Case-parent owns a hidden canvas that periodically rasterises the
  // sub-app iframe's main visual element (canvas or SVG) at ~10 fps,
  // then captureStream(15)s it onto the pre-allocated screen
  // transceiver via setScreenTrack(). The trainer's pc.ontrack picks
  // up the second m-line and renders it in 個案螢幕鏡像 tile.
  const iframeRef  = useRef<HTMLIFrameElement>(null);
  const mirrorCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (state.phase !== "live") return;
    if (!subappSrc) {
      // No sub-app configured — drop screen track entirely so trainer
      // sees the placeholder tile instead of a black frame mistaken
      // for a frozen game canvas.
      setScreenTrack(null).catch(() => { /* */ });
      return;
    }

    // Cross-origin iframe DOM access is blocked by browser same-origin
    // policy. Forked sub-apps (cognihome-rppg / -myndscape / -soramynd
    // .pages.dev) cannot be DOM-scraped from the home.sigmacog.xyz
    // parent, so the captureStream-of-iframe-canvas approach silently
    // produces dark frames. Detect and skip — trainer's screen tile
    // shows the explicit "鏡像不適用" placeholder instead of black.
    // Real cross-origin mirror needs the fork to send frames via
    // postMessage (P7 hardening item).
    let canIntrospectIframe = false;
    try {
      // Touching contentDocument throws SecurityError on cross-origin.
      // Same-origin (mock subapps under /subapp/*) returns the document.
      canIntrospectIframe = !!iframeRef.current?.contentDocument;
    } catch { canIntrospectIframe = false; }
    if (!canIntrospectIframe) {
      setScreenTrack(null).catch(() => { /* */ });
      return;
    }

    // 2026-05-09 fix: previous implementation always copied iframe DOM
    // → parent 2D canvas → captureStream. That works for SVG / 2D canvas
    // sources but breaks for **WebGL/WebGPU canvases** (Pixi.js, Three.js)
    // because the default `preserveDrawingBuffer: false` makes external
    // drawImage() return an empty buffer outside the render call. Soramynd
    // uses Pixi.js so trainer always saw a black tile during games.
    //
    // New strategy:
    //   - Canvas source: call iframe-canvas.captureStream() DIRECTLY.
    //     captureStream taps the rendering pipeline internally and works
    //     for WebGL/WebGPU regardless of preserveDrawingBuffer.
    //   - SVG source: keep the previous serialise → drawImage poll loop
    //     into a parent 2D canvas (no equivalent direct stream API for
    //     SVG).
    //
    // We poll for the iframe canvas on a short interval until it appears,
    // then settle on the direct-captureStream path. If it's a fresh DOM
    // mount (e.g. trainer re-armed task), we tear down and re-poll.
    let alive = true;
    let pollTimer: number | null = null;
    let activeStream: MediaStream | null = null;
    let activeSourceEl: Element | null = null;

    // SVG fallback state — only allocated when we settle on an SVG source.
    let svgCanvas: HTMLCanvasElement | null = null;
    let svgCtx: CanvasRenderingContext2D | null = null;
    let svgImg: HTMLImageElement | null = null;
    let svgRaf: number | null = null;

    function findSourceEl(): SVGElement | HTMLCanvasElement | null {
      try {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return null;
        return (doc.querySelector("canvas") as HTMLCanvasElement | null)
            ?? (doc.querySelector("svg") as SVGElement | null);
      } catch { return null; }
    }

    function teardownActive() {
      if (activeStream) {
        try { activeStream.getTracks().forEach(t => t.stop()); } catch { /* */ }
        activeStream = null;
      }
      if (svgRaf !== null) { window.clearTimeout(svgRaf); svgRaf = null; }
      svgCanvas = null;
      svgCtx = null;
      svgImg = null;
      activeSourceEl = null;
    }

    function bindCanvasDirect(el: HTMLCanvasElement) {
      // Direct captureStream — works for WebGL/WebGPU/2D alike.
      try {
        const stream = el.captureStream(15);
        const track = stream.getVideoTracks()[0];
        if (!track) return;
        activeStream = stream;
        activeSourceEl = el;
        setScreenTrack(track).catch(() => { /* */ });
      } catch (e) {
        console.warn("[screen-mirror] canvas.captureStream failed", e);
      }
    }

    function bindSvgFallback(el: SVGElement) {
      svgCanvas = document.createElement("canvas");
      svgCanvas.width  = 800;
      svgCanvas.height = 450;
      mirrorCanvasRef.current = svgCanvas;
      svgCtx = svgCanvas.getContext("2d");
      if (!svgCtx) return;
      svgCtx.fillStyle = "#1c1814";
      svgCtx.fillRect(0, 0, svgCanvas.width, svgCanvas.height);
      const stream = svgCanvas.captureStream(15);
      const track = stream.getVideoTracks()[0];
      if (track) {
        activeStream = stream;
        activeSourceEl = el;
        setScreenTrack(track).catch(() => { /* */ });
      }
      const tick = async () => {
        if (!alive || !svgCtx || !svgCanvas) return;
        try {
          const xml = new XMLSerializer().serializeToString(el);
          const url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
          if (!svgImg) svgImg = new Image();
          await new Promise<void>((resolve) => {
            svgImg!.onload = () => {
              svgCtx!.fillStyle = "#1c1814";
              svgCtx!.fillRect(0, 0, svgCanvas!.width, svgCanvas!.height);
              svgCtx!.drawImage(svgImg!, 0, 0, svgCanvas!.width, svgCanvas!.height);
              resolve();
            };
            svgImg!.onerror = () => resolve();
            svgImg!.src = url;
          });
        } catch { /* */ }
        svgRaf = window.setTimeout(tick, 100) as unknown as number;
      };
      tick();
    }

    function poll() {
      if (!alive) return;
      const el = findSourceEl();
      // Re-bind only on element identity change (sub-app re-mount).
      if (el !== activeSourceEl) {
        // Cross-realm `instanceof HTMLCanvasElement` returns false for nodes
        // adopted from same-origin child frames — each iframe owns its own
        // realm with its own HTMLCanvasElement constructor. Tag check is
        // realm-agnostic. Without this, every canvas (Pixi/WebGL/2D) fell
        // through to bindSvgFallback, which serialized the canvas markup as
        // invalid SVG and rendered a permanent dark frame to the trainer.
        const tag = el?.tagName?.toUpperCase();
        console.debug("[screen-mirror] findSourceEl →", tag ?? "null",
          el ? `${(el as Element).id || "(no-id)"} ${el.className || ""}` : "");
        teardownActive();
        if (tag === "CANVAS") bindCanvasDirect(el as HTMLCanvasElement);
        else if (tag === "SVG") bindSvgFallback(el as SVGElement);
      }
      pollTimer = window.setTimeout(poll, 1000) as unknown as number;
    }
    poll();

    return () => {
      alive = false;
      if (pollTimer !== null) window.clearTimeout(pollTimer);
      teardownActive();
      setScreenTrack(null).catch(() => { /* */ });
    };
  }, [state.phase, subappSrc, setScreenTrack]);

  return (
    <div style={{ height: "100vh", background: "var(--ink)", color: "var(--paper)", position: "relative" }}>
      {/* Sub-app iframe (full background). Rendered only when trainer
          has configured a source/game; otherwise show "等候配置"
          placeholder. Never falls back to a stand-in game whose
          synthesized metrics could deceive the case (H4 fix). */}
      {subappSrc ? (
        <iframe
          ref={iframeRef}
          src={subappSrc}
          title="sub-app"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          allow="autoplay; camera; microphone; display-capture; serial; usb; bluetooth"
          // Audit 2026-05-09 P2-E REVERTED: sandbox attribute removed.
          // Even with allow-same-origin set, sandboxed iframes
          // intermittently break BroadcastChannel and canvas
          // captureStream() on cross-engine paths (Chromium ↔ WebKit),
          // which broke trainer's screen-mirror feed AND the rPPG /
          // sub-app metric stream end-to-end. Sub-apps load from CF
          // Pages projects we control (cognihome-rppg / -soramynd /
          // -myndscape); the original "allow-top-navigation block"
          // threat is not currently mitigated by anything else, but
          // restoring functional video + metrics outweighs that
          // defense-in-depth. Treat as a known TODO.
        />
      ) : (
        <CaseAwaitingConfig />
      )}

      {/* P8-4: hidden second iframe — when an active task switched the
          main iframe away from rPPG, run cognihome-rppg in headless mode
          to keep HRV / SpO₂ monitoring flowing for the trainer. Same
          origin via /sub/rppg/ proxy → BroadcastChannel `tr-{room}` joins
          the room same as main, so emitted metrics flow through the
          parent's BC bridge to WS → trainer's right-rail panel. headless=1
          suppresses the case-side status badge and the `reward` channel
          (the active task owns reward; rPPG just adds vitals). */}
      {state.phase === "live" && state.activeTask && !subappSrc?.startsWith("/sub/rppg") && (
        <iframe
          src={`/sub/rppg/?telerehab=1&room=${sessionId}&headless=1`}
          title="rppg-bg"
          style={{
            position: "absolute", width: 1, height: 1,
            opacity: 0, pointerEvents: "none", border: 0, left: -9999, top: -9999,
          }}
          allow="autoplay; camera; microphone"
          // Audit 2026-05-09 P2-E REVERTED: see primary iframe comment.
          aria-hidden="true"
        />
      )}

      {/* Trainer webcam thumbnail bottom-right */}
      <div style={{
        position: "absolute", bottom: 16, right: 16,
        width: 200, aspectRatio: "4 / 3",
        border: "1px solid var(--paper)", borderRadius: "var(--r-sm)",
        overflow: "hidden", background: "#1c1814",
        pointerEvents: "none",
      }}>
        <VideoElement stream={trainerStream} mirror={false} />
        <div style={{
          position: "absolute", top: 6, left: 6,
          background: "rgba(0,0,0,0.5)", color: "var(--paper)",
          fontFamily: "var(--mono)", fontSize: 10, padding: "2px 6px",
        }}>訓練師</div>
      </div>

      {/* Top status strip */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "8px var(--s-5)",
        background: "rgba(28,24,20,0.6)", color: "var(--paper)",
      }}>
        <span style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", letterSpacing: "0.1em" }}>
          COGNIHOME · LIVE · SID {sessionId.slice(0, 12)}
          {rec.recording && (
            <span style={{ marginLeft: 12, color: "var(--rust)" }}>
              ● REC · {rec.uploadedChunks} chunks
              {rec.failedChunks > 0 && <span style={{ color: "var(--warning)" }}> ({rec.failedChunks} failed)</span>}
            </span>
          )}
          {(rawHealth.uploaded + rawHealth.failed) > 0 && (
            <span style={{ marginLeft: 12, color: "var(--olive)" }}>
              EEG↑ {rawHealth.uploaded}
              {rawHealth.failed > 0 && <span style={{ color: "var(--warning)" }}> ({rawHealth.failed} failed)</span>}
            </span>
          )}
        </span>
        <button onClick={() => {
          if (confirm("結束訓練？")) send({ type: "emergency_stop" });
        }} style={{
          background: "transparent", color: "var(--paper-deep)",
          border: "1px solid var(--paper-deep)", padding: "4px 10px",
          fontSize: "var(--t-xs)", cursor: "pointer", borderRadius: 3,
        }}>緊急停止</button>
      </div>
    </div>
  );
}

/* ─── small components ─── */
function VideoTile({ label, stream, mirror, fallback, local }: {
  label: string; stream: MediaStream | null; mirror: boolean; fallback: string; local?: boolean;
}) {
  return (
    <div>
      <div className="serial" style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span>{label}</span>
        {stream && <span style={{ color: "var(--olive)" }}>● live</span>}
      </div>
      <div style={{
        aspectRatio: "4 / 3",
        border: "1px solid var(--ink)",
        borderRadius: "var(--r-md)",
        overflow: "hidden",
        background: "#1c1814",
        position: "relative",
        boxShadow: "4px 4px 0 var(--ink)",
      }}>
        {stream ? <VideoElement stream={stream} mirror={mirror} muted={local} /> : (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            color: "var(--paper-deep)", fontFamily: "var(--mono)", fontSize: "var(--t-sm)",
            padding: "var(--s-4)", textAlign: "center",
          }}>{fallback}</div>
        )}
      </div>
    </div>
  );
}

/* P8-1: DevicePicker — small dropdown for switching camera/mic source.
   Hides itself when there are zero or one devices available (nothing to
   pick). Lazy-renders to avoid layout shift in lobby on first paint. */
function DevicePicker({ label, devices, selected, onSelect, placeholder }: {
  label: string;
  devices: { deviceId: string; label: string }[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  placeholder: string;
}) {
  if (devices.length === 0) {
    return (
      <div style={{ fontSize: "var(--t-xs)", color: "var(--ink-soft)", fontFamily: "var(--mono)" }}>
        {label} · 偵測中…
      </div>
    );
  }
  return (
    <div>
      <div className="serial" style={{ marginBottom: 4 }}>{label}</div>
      <select
        value={selected ?? ""}
        onChange={(e) => onSelect(e.target.value || null)}
        style={{
          width: "100%",
          padding: "6px 10px",
          fontSize: "var(--t-sm)",
          fontFamily: "var(--mono)",
          background: "var(--paper)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--r-sm)",
        }}>
        <option value="">{placeholder}</option>
        {devices.map(d => (
          <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
        ))}
      </select>
    </div>
  );
}

function VideoElement({ stream, mirror, muted }: { stream: MediaStream | null; mirror: boolean; muted?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  // Browsers block autoplay of unmuted media without prior user gesture.
  // Remote peer video is intentionally unmuted (so trainer + case can hear
  // each other), so first render in lobby may freeze on the first frame
  // until the user clicks something. We explicitly call .play() and on
  // rejection surface a click-to-play overlay so the case isn't stuck
  // staring at a black tile wondering "why can't I see the trainer".
  const [needsTap, setNeedsTap] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !stream) return;
    el.srcObject = stream;
    let cancelled = false;
    const tryPlay = async () => {
      try {
        await el.play();
        if (!cancelled) setNeedsTap(false);
      } catch (err) {
        // NotAllowedError — autoplay blocked. Show overlay; user click
        // dispatches another play() call which is allowed (gesture-bound).
        if (!cancelled) setNeedsTap(true);
        console.warn("[VideoElement] autoplay blocked:", (err as Error).message);
      }
    };
    tryPlay();
    return () => { cancelled = true; };
  }, [stream]);
  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <video
        ref={ref}
        autoPlay
        playsInline
        muted={muted}
        onClick={() => {
          // Manual play attempt within a user gesture — always allowed.
          ref.current?.play().then(() => setNeedsTap(false)).catch(() => { /* */ });
        }}
        style={{
          width: "100%", height: "100%", objectFit: "cover",
          transform: mirror ? "scaleX(-1)" : undefined,
          cursor: needsTap ? "pointer" : undefined,
        }}
      />
      {needsTap && (
        <div
          onClick={() => {
            ref.current?.play().then(() => setNeedsTap(false)).catch(() => { /* */ });
          }}
          style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexDirection: "column", gap: 8,
            background: "rgba(28,24,20,0.55)",
            color: "var(--paper)",
            fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
            cursor: "pointer",
            letterSpacing: "0.08em",
          }}>
          <span style={{ fontSize: 24 }}>▶</span>
          <span>點此播放（瀏覽器擋自動播放）</span>
        </div>
      )}
    </div>
  );
}

function VideoBox({ stream, fallback }: { stream: MediaStream | null; fallback: string }) {
  return (
    <div style={{
      aspectRatio: "4 / 3",
      border: "1px solid var(--ink)",
      borderRadius: "var(--r-md)",
      overflow: "hidden",
      background: "#1c1814",
      position: "relative",
    }}>
      {stream ? <VideoElement stream={stream} mirror={false} /> : (
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center",
          color: "var(--paper-deep)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        }}>{fallback}</div>
      )}
    </div>
  );
}

function PlaceholderBox({ text }: { text: string }) {
  return (
    <div style={{
      aspectRatio: "4 / 3",
      border: "1px dashed var(--hairline)",
      borderRadius: "var(--r-md)",
      background: "var(--paper-deep)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "var(--ink-faint)", fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
      textAlign: "center", padding: "var(--s-4)",
    }}>{text}</div>
  );
}

function RewardPanelLive({ state, metrics }: {
  state: RoomState;
  metrics: Record<string, { value: number; ts: number }>;
}) {
  const reward = metrics["reward"]?.value;
  const alpha  = metrics["eeg.alpha"]?.value;
  const ts     = metrics["reward"]?.ts;
  const stale  = ts ? (Date.now() - ts > 3000) : true;

  return (
    <div style={{
      background: "var(--ink)", color: "var(--paper)",
      padding: "var(--s-4)", borderRadius: "var(--r-md)",
      display: "flex", flexDirection: "column", justifyContent: "center",
    }}>
      <div className="row row--between" style={{ marginBottom: 6 }}>
        <div className="serial" style={{ color: "var(--paper-deep)" }}>Now · live</div>
        <span style={{ fontSize: "var(--t-xs)", color: stale ? "var(--warning)" : "var(--olive)" }}>
          {stale ? "● stale" : "● fresh"}
        </span>
      </div>
      <div className="serial" style={{ color: "var(--rust)", fontSize: 9, marginBottom: 4 }}>reward</div>
      <div style={{
        fontFamily: "var(--mono)", fontSize: "var(--t-2xl)", fontWeight: 600,
        color: "var(--rust)", lineHeight: 1,
      }}>{reward !== undefined ? reward.toFixed(2) : "—"}</div>
      <div style={{ fontSize: "var(--t-xs)", color: "var(--paper-deep)", marginTop: 6 }}>
        alpha: <span style={{ color: "var(--paper)", fontFamily: "var(--mono)" }}>{alpha !== undefined ? alpha.toFixed(2) : "—"}</span> µV²
      </div>
      <div style={{
        fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        color: "var(--paper-deep)", marginTop: "var(--s-2)",
      }}>
        source: {state.source ?? "—"}<br />
        game:   {state.game ?? "—"}
      </div>
    </div>
  );
}

// Protocol presets — names + parameter bundles per /demo design lock.
// Each preset applies a source + game + thresholds in one fan-out of WS
// messages. (Reward formula slot removed 2026-05-09 per user — sub-app
// reward logic is hardcoded per game.)
const PRESETS = {
  "alpha-uplift": {
    label: "alpha-uplift · 注意力",
    source: "eeg",
    game: "soramynd-plane",
    params: { T_a: 12, T_b: 2.5, T_h: 40, eps: 0.30 },
  },
  "theta-beta": {
    label: "theta-beta · 抑制",
    source: "eeg",
    game: "soramynd-baseball",
    params: { T_a: 10, T_b: 2.0, T_h: 40, eps: 0.30 },
  },
  "hrv-breath": {
    label: "HRV · 呼吸調節",
    source: "hrv",
    game: "visiomynd-breath",
    params: { T_a: 12, T_b: 2.5, T_h: 50, eps: 0.30 },
  },
  "custom": {
    label: "custom · 自訂",
    source: "baseline",
    game: "",
    params: { T_a: 12, T_b: 2.5, T_h: 40, eps: 0.30 },
  },
} as const;
type PresetKey = keyof typeof PRESETS;

function matchPreset(state: RoomState): PresetKey | null {
  for (const [k, p] of Object.entries(PRESETS) as [PresetKey, typeof PRESETS[PresetKey]][]) {
    if (state.source === p.source && state.game === p.game) return k;
  }
  return null;
}

const SOURCE_ALT: Record<string, string> = {
  eeg: "1000 SPS · 8ch",
  hrv: "rPPG · 30 fps",
  baseline: "EEG 純錄無 game",
  behavior: "task accuracy / RT",
  none: "metric-only 觀察",
};
const GAME_ALT: Record<string, string> = {
  "soramynd-plane":      "alpha · altitude",
  "soramynd-baseball":   "θ/β · pitch power",
  "soramynd-zentangle":  "alpha · stroke",
  "soramynd-karesansui": "alpha · sand pattern",
  "visiomynd-breath":    "rmssd · breathing pace",
  "myndscape-task":      "12 cognitive tasks",
  "velomynd-acpt":       "Animal CPT 注意力",
};

const selectorStyle: CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: "var(--t-sm)",
  fontFamily: "var(--mono)",
  background: "var(--paper)",
  border: "1px solid var(--hairline)",
  borderRadius: "var(--r-sm)",
};
const altSubStyle: CSSProperties = {
  fontSize: "var(--t-xs)",
  fontFamily: "var(--mono)",
  color: "var(--ink-faint)",
  marginTop: 4,
};

// P8-3: trainer's own custom presets loaded from API at Studio mount.
// These render below the four built-ins in the right rail and apply
// the same way (set_source / set_game / param_update fan-out).
interface CustomPreset {
  id: string;
  name: string;
  source: string | null;
  game:   string | null;
  params: Record<string, unknown>;
}

/**
 * RppgLivePanel — trainer-side live readout of cognihome-rppg metrics.
 * Reads the metric keys cognihome-rppg/js/app.js _cogniEmitMetric()
 * publishes: hrv.{hr,rmssd,sdnn,pnn50,lfhf}, vitals.{spo2,respRate,si},
 * beats.count. Stale tag flips to ●stale when last update > 4 s old
 * (rPPG emits at ~5 s cadence per its live-analysis loop).
 */
function RppgLivePanel({ metrics }: {
  metrics: Record<string, { value: number; ts: number }>;
}) {
  const m = (k: string) => metrics[k];
  const hr     = m("hrv.hr");
  const rmssd  = m("hrv.rmssd");
  const sdnn   = m("hrv.sdnn");
  const pnn50  = m("hrv.pnn50");
  const lfhf   = m("hrv.lfhf");
  const spo2   = m("vitals.spo2");
  const resp   = m("vitals.respRate");
  const si     = m("vitals.si");

  const newest = Math.max(
    hr?.ts ?? 0, rmssd?.ts ?? 0, sdnn?.ts ?? 0, pnn50?.ts ?? 0,
    lfhf?.ts ?? 0, spo2?.ts ?? 0, resp?.ts ?? 0, si?.ts ?? 0,
  );
  const fresh  = newest > 0 && (Date.now() - newest) < 4000;
  const empty  = newest === 0;

  const fmt = (mv: { value: number } | undefined, decimals = 0) =>
    mv?.value !== undefined && Number.isFinite(mv.value)
      ? mv.value.toFixed(decimals)
      : "—";

  const cellStyle: React.CSSProperties = {
    padding: "6px 8px",
    background: "var(--paper-warm)",
    border: "1px solid var(--hairline)",
    borderRadius: "var(--r-sm)",
    minWidth: 0,
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: "var(--mono)", fontSize: 10,
    color: "var(--ink-soft)", textTransform: "uppercase",
    letterSpacing: "0.04em",
  };
  const valueStyle: React.CSSProperties = {
    fontFamily: "var(--mono)", fontSize: "var(--t-base)",
    fontWeight: 600, color: "var(--ink)", lineHeight: 1.2,
  };
  const unitStyle: React.CSSProperties = {
    fontSize: 10, color: "var(--ink-faint)", marginLeft: 2,
  };

  return (
    <div>
      <div className="row row--between" style={{ marginBottom: 6 }}>
        <div className="serial">VisioMynd · 即時生理</div>
        <span style={{
          fontSize: "var(--t-xs)",
          color: empty ? "var(--ink-faint)" : fresh ? "var(--olive)" : "var(--warning)",
          fontFamily: "var(--mono)",
        }}>
          {empty ? "● 等候訊號…" : fresh ? "● live" : "● stale"}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4 }}>
        <div style={cellStyle}>
          <div style={labelStyle}>HR</div>
          <div style={valueStyle}>{fmt(hr, 0)}<span style={unitStyle}>bpm</span></div>
        </div>
        <div style={cellStyle}>
          <div style={labelStyle}>SpO₂</div>
          <div style={valueStyle}>{fmt(spo2, 0)}<span style={unitStyle}>%</span></div>
        </div>
        <div style={cellStyle}>
          <div style={labelStyle}>Resp</div>
          <div style={valueStyle}>{fmt(resp, 0)}<span style={unitStyle}>/min</span></div>
        </div>
        <div style={cellStyle}>
          <div style={labelStyle}>SI</div>
          <div style={valueStyle}>{fmt(si, 0)}</div>
        </div>
        <div style={cellStyle}>
          <div style={labelStyle}>RMSSD</div>
          <div style={valueStyle}>{fmt(rmssd, 0)}<span style={unitStyle}>ms</span></div>
        </div>
        <div style={cellStyle}>
          <div style={labelStyle}>SDNN</div>
          <div style={valueStyle}>{fmt(sdnn, 0)}<span style={unitStyle}>ms</span></div>
        </div>
        <div style={cellStyle}>
          <div style={labelStyle}>pNN50</div>
          <div style={valueStyle}>{fmt(pnn50, 0)}<span style={unitStyle}>%</span></div>
        </div>
        <div style={cellStyle}>
          <div style={labelStyle}>LF/HF</div>
          <div style={valueStyle}>{fmt(lfhf, 2)}</div>
        </div>
      </div>
    </div>
  );
}

function RightRailLive({ state, send, caseSubappState, metrics }: {
  state: RoomState;
  send: (msg: any) => void;
  caseSubappState: string | null;
  metrics: Record<string, { value: number; ts: number }>;
}) {
  // 2026-05-09 redesign pass-1: dead Protocol presets / Source / Game /
  // Thresholds UI removed per user. The full 5-app picker + per-app
  // params card flow is in the next pass; this commit just strips the
  // non-functional widgets so the rail is honest about what works.
  // Kept: rPPG live panel, Task control (only working selector),
  // Action (pause/stop).
  return (
    <>
      {/* rPPG live signals — visiomynd auto-loads as the always-on
          baseline; metrics flow through tr-{room} BC → WS → here. */}
      <RppgLivePanel metrics={metrics} />

      {/* Task — armed-vs-running task slot. (Will be subsumed into the
          per-app params card in pass-2.) */}
      <TaskControl state={state} send={send} caseSubappState={caseSubappState} />

      {/* Action — Run / Pause / Stop */}
      <div>
        <div className="serial" style={{ marginBottom: 6 }}>Action</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.4fr", gap: 6 }}>
          <button className="btn btn--ghost btn--small" disabled style={{ justifyContent: "center" }}>
            ▶ {state.phase === "live" ? "Running" : state.phase === "paused" ? "Paused" : state.phase}
          </button>
          <button className="btn btn--ghost btn--small"
                  onClick={() => send({ type: state.phase === "paused" ? "resume" : "pause" })}
                  style={{ justifyContent: "center" }}>
            {state.phase === "paused" ? "▶ Resume" : "⏸ Pause"}
          </button>
          <button className="btn btn--small"
                  onClick={() => { if (confirm("結束此 session 並儲存？")) send({ type: "end_session" }); }}
                  style={{
                    background: "var(--paper-warm)", color: "var(--danger)",
                    borderColor: "var(--danger)", justifyContent: "center",
                  }}>✋ Stop &amp; save</button>
        </div>
      </div>

      {/* Audit 2026-05-09 P1-A pass-2: removed 2×2 "open in new tab"
          shortcuts that linked at https://www.sigmacog.xyz/{soramynd,
          visiomynd,myndscape,eeg}. Those bounced trainer to the
          research-line consumer site, violating the CogniHome ↔
          sigmacog isolation rule. Trainers stay inside Studio; the
          embedded iframe is the only sub-app surface. */}
    </>
  );
}

/* ─── TaskControl — armed-vs-running task panel ──────────────────────────────
   User flow (memory project_cognihome.md "Sub-app 操作行為定義"):
     trainer picks task → 「執行」 (arm_task) → case window loads task UI but
     does NOT start → trainer presses 「開始訓練」 (start_task) → case starts
     → on completion sub-app emits task_complete → trainer sees results +
     「結束此 task」 to return to monitoring mode.
   12 myndscape tasks (cognitive) + 4 soramynd tasks (NFB w/ EEG hardware).
   Basic level only per user 2026-05-09. Real cognitive task UI lives in
   cognihome-myndscape; NFB games + EEG connection in cognihome-soramynd. */
const SORAMYND_TASKS: Array<{ id: string; label: string }> = [
  { id: "soramynd:plane",       label: "🛩 SoraMynd · 紙飛機（EEG）" },
  { id: "soramynd:baseball",    label: "⚾ SoraMynd · 棒球（EEG）" },
  { id: "soramynd:zentangle",   label: "🌀 SoraMynd · Zentangle（EEG）" },
  { id: "soramynd:karesansui",  label: "🪨 SoraMynd · 枯山水（EEG）" },
];
const MYNDSCAPE_TASKS: Array<{ id: string; label: string }> = [
  { id: "myndscape:n-back",       label: "01 · N-Back" },
  { id: "myndscape:stroop",       label: "02 · Stroop Color-Word" },
  { id: "myndscape:digit-span",   label: "03 · Digit Span" },
  { id: "myndscape:wcst",         label: "04 · Wisconsin Card Sorting" },
  { id: "myndscape:trail-making", label: "05 · Trail Making" },
  { id: "myndscape:corsi-block",  label: "06 · Corsi Block-Tapping" },
  { id: "myndscape:pursuit-rotor",label: "07 · Pursuit Rotor" },
  { id: "myndscape:tower-london", label: "08 · Tower of London" },
  { id: "myndscape:mental-rotate",label: "09 · Mental Rotation" },
  { id: "myndscape:perspective",  label: "10 · Perspective Taking (SOT)" },
  { id: "myndscape:ant",          label: "11 · Attention Network" },
  { id: "myndscape:iowa-gambling",label: "12 · Iowa Gambling" },
];

function TaskControl({ state, send, caseSubappState }: {
  state: RoomState;
  send: (msg: any) => void;
  caseSubappState: string | null;
}) {
  // Soramynd tasks listed first (EEG-driven NFB), then myndscape (cognitive).
  const ALL_TASKS = [...SORAMYND_TASKS, ...MYNDSCAPE_TASKS];
  const [pickedTask, setPickedTask] = useState<string>(SORAMYND_TASKS[0].id);
  const taskState  = state.taskState  ?? null;
  const activeTask = state.activeTask ?? null;
  const liveSession = state.phase === "live" || state.phase === "paused";
  const isSoramynd  = activeTask?.startsWith("soramynd:") ?? false;

  if (!liveSession) {
    return (
      <div>
        <div className="serial" style={{ marginBottom: 6 }}>Task</div>
        <div className="muted" style={{ fontSize: "var(--t-xs)", color: "var(--ink-soft)" }}>
          訓練 task 進入 session 後才能裝載
        </div>
      </div>
    );
  }

  // No task armed — show picker
  if (taskState === null || taskState === undefined) {
    return (
      <div>
        <div className="serial" style={{ marginBottom: 6 }}>Task · 裝載</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 6 }}>
          <select value={pickedTask} onChange={(e) => setPickedTask(e.target.value)}
                  className="select" style={{ width: "100%", fontSize: "var(--t-xs)" }}>
            <optgroup label="SoraMynd · NFB (EEG)">
              {SORAMYND_TASKS.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </optgroup>
            <optgroup label="MyndScape · 認知作業">
              {MYNDSCAPE_TASKS.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </optgroup>
          </select>
          <button className="btn btn--small"
                  onClick={() => send({ type: "arm_task", task: pickedTask, params: { level: "basic" } })}
                  style={{ justifyContent: "center", whiteSpace: "nowrap" }}>
            執行 ▷
          </button>
        </div>
        <div className="muted" style={{ fontSize: "var(--t-xs)", marginTop: 4 }}>
          按「執行」載入 task UI 到個案視窗（不開始）
        </div>
      </div>
    );
  }

  const taskLabel = ALL_TASKS.find(t => t.id === activeTask)?.label ?? activeTask ?? "—";

  // Armed — task loaded, awaiting trainer go.
  // For soramynd (EEG-driven), surface explicit hint that case must
  // click 「連線 EEG」 in their iframe before pressing 開始訓練 will work.
  // 2026-05-09 fix: gate 開始訓練 on case's hw_paired state so trainer
  // can't fire start_task before the case has finished pairing the EEG
  // device. Sub-app emits subapp_state with state in
  // {hw_paired, paired_idle, armed, running} once Web Serial is open.
  // Anything else (including null = no signal yet) blocks the button.
  const eegStates = new Set(["hw_paired", "paired_idle", "armed", "running"]);
  const eegReady = caseSubappState !== null && eegStates.has(caseSubappState);
  const blockStart = isSoramynd && !eegReady;
  if (taskState === "armed") {
    return (
      <div>
        <div className="serial" style={{ marginBottom: 6 }}>Task · 已裝載</div>
        <div style={{
          padding: "var(--s-2) var(--s-3)", marginBottom: 6,
          background: "var(--paper-warm)", border: "1px solid var(--ink-soft)",
          fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        }}>
          ◷ {taskLabel} · 等待開始
        </div>
        {isSoramynd && (
          <div style={{
            padding: "var(--s-2) var(--s-3)", marginBottom: 6,
            background: blockStart ? "rgba(196, 71, 31, 0.08)" : "rgba(94, 116, 38, 0.10)",
            border: `1px solid ${blockStart ? "var(--rust)" : "var(--olive)"}`,
            fontSize: "var(--t-xs)", color: blockStart ? "var(--rust)" : "var(--olive)",
            lineHeight: 1.5,
          }}>
            {blockStart
              ? "⚠ 個案端尚未連線 EEG。請語音引導：「請按下視窗中的『連線 EEG』按鈕，選擇你的 USB Serial 裝置」。連線完成本面板會自動解鎖。"
              : "✓ 個案端 EEG 已連線。可以按「開始訓練」。"}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          <button className="btn btn--small"
                  disabled={blockStart}
                  onClick={() => send({ type: "start_task" })}
                  style={{
                    background: blockStart ? "var(--paper-deep)" : "var(--ink)",
                    color: blockStart ? "var(--ink-faint)" : "var(--paper)",
                    borderColor: blockStart ? "var(--hairline)" : "var(--ink)",
                    cursor: blockStart ? "not-allowed" : "pointer",
                    justifyContent: "center",
                  }}>
            ▶ 開始訓練
          </button>
          <button className="btn btn--ghost btn--small"
                  onClick={() => {
                    // Cancel-arm: reuse task_complete with empty results so
                    // the slot returns to null. Avoids needing a separate
                    // ws message just to clear the armed slot.
                    if (confirm("取消已裝載的 task？")) {
                      send({ type: "task_complete", task: activeTask ?? "unknown", results: { aborted: true } });
                    }
                  }}
                  style={{ justifyContent: "center" }}>
            ✕ 取消
          </button>
        </div>
      </div>
    );
  }

  // Running — task in progress on case side. Trainer can abort mid-run
  // if individual / planning needs change (user 2026-05-09).
  if (taskState === "running") {
    return (
      <div>
        <div className="serial" style={{ marginBottom: 6 }}>Task · 進行中</div>
        <div style={{
          padding: "var(--s-2) var(--s-3)", marginBottom: 6,
          background: "var(--ink)", color: "var(--paper)",
          fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
        }}>
          ▶ {taskLabel}
        </div>
        <div className="muted" style={{ fontSize: "var(--t-xs)", marginBottom: 6 }}>
          個案執行中。完成時自動回報；也可手動中斷換別的任務。
        </div>
        <button className="btn btn--ghost btn--small"
                onClick={() => {
                  if (confirm("中斷此任務？個案視窗會回到等候畫面。")) {
                    send({ type: "task_complete", task: activeTask ?? "unknown", results: { aborted: true } });
                  }
                }}
                style={{ width: "100%", justifyContent: "center", color: "var(--danger)", borderColor: "var(--danger)" }}>
          ✕ 中斷訓練
        </button>
      </div>
    );
  }

  // Completed — show summary + button to clear back to picker
  return (
    <div>
      <div className="serial" style={{ marginBottom: 6 }}>Task · 完成</div>
      <div style={{
        padding: "var(--s-2) var(--s-3)", marginBottom: 6,
        background: "var(--paper-warm)", border: "1px solid var(--olive)",
        fontFamily: "var(--mono)", fontSize: "var(--t-xs)",
      }}>
        ✓ {taskLabel}
      </div>
      <button className="btn btn--ghost btn--small"
              onClick={() => {
                // Re-arm same task type or pick another — emit a "soft reset"
                // task_complete-with-cleared so the picker returns. The DO
                // currently leaves taskState=completed until next arm_task,
                // so any new arm flow naturally clears it.
                send({ type: "arm_task", task: pickedTask, params: { level: "basic" } });
              }}
              style={{ width: "100%", justifyContent: "center" }}>
        ↻ 裝載下一個 task
      </button>
    </div>
  );
}

function ThresholdSlider({ label, k, min, max, step, decimals, fallback, state, send }: {
  label: string; k: string; min: number; max: number; step: number; decimals: number;
  fallback: number; state: RoomState; send: (msg: any) => void;
}) {
  const v = (state.params[k] as number) ?? fallback;
  return (
    <div>
      <div style={{ fontFamily: "var(--mono)", fontSize: "var(--t-xs)", color: "var(--ink-soft)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: "var(--mono)", fontSize: "var(--t-sm)", marginBottom: 4 }}>{v.toFixed(decimals)}</div>
      <input type="range" min={min} max={max} step={step} value={v}
             onChange={(e) => send({ type: "param_update", key: k, value: parseFloat(e.target.value) })}
             style={{ width: "100%" }} />
    </div>
  );
}

// AppLinkLive removed 2026-05-09 — its only call sites linked back to
// www.sigmacog.xyz which violates CogniHome ↔ sigmacog isolation.

function HiddenLocalVideo({ stream }: { stream: MediaStream | null }) {
  // Hidden but mounted so getUserMedia stream stays alive + tracks negotiate
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => { if (ref.current && stream) ref.current.srcObject = stream; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted style={{ display: "none" }} />;
}

/**
 * Map trainer's source + game selection to a sub-app URL. Source
 * dominates when "hrv" or game is myndscape; otherwise SoraMynd-style
 * game runs as the case-side surface (with sgimacog/EEG as headless
 * source per spec — for P5 demo we just show the game and emit metrics
 * matching the source picked).
 */
// Raw EEG R2 upload helpers — case-parent calls these on raw_preview
// flush + on phase=ended. Dormant if no sub-app ever emits raw_preview.
// H3 fix: retry once on transient failure, return success bool so the
// caller can surface failed seq counts in the LIVE strip. The retried
// request keeps the same seq so the server-side finalize HEAD pass
// finds every chunk in order.
async function flushRawEegWithRetry(sessionId: string, seq: number, samples: number[]): Promise<boolean> {
  if (samples.length === 0) return true;
  const tok = sessionStorage.getItem("cognihome_access_token");
  const buf = new Float32Array(samples).buffer;
  const url = `${import.meta.env.PROD ? "https://cognihome-api.swlucifer.workers.dev" : "http://localhost:8787"}/api/sessions/${sessionId}/raw-eeg/chunk?seq=${seq}`;
  const init: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/octet-stream", Authorization: `Bearer ${tok ?? ""}` },
    body: buf,
  };
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(url, init);
      if (r.ok) return true;
      // 4xx auth/validation: retrying won't help; bail.
      if (r.status >= 400 && r.status < 500) return false;
    } catch { /* network — fall through to retry */ }
    if (attempt === 0) await new Promise(r => setTimeout(r, 1500));
  }
  return false;
}

async function finalizeRawEeg(
  sessionId: string,
  count: number,
  channels: string[],
  sps: number,
  startedAt: string | null,
): Promise<boolean> {
  if (count === 0) return true;
  const tok = sessionStorage.getItem("cognihome_access_token");
  const url = `${import.meta.env.PROD ? "https://cognihome-api.swlucifer.workers.dev" : "http://localhost:8787"}/api/sessions/${sessionId}/raw-eeg/finalize`;
  const init: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok ?? ""}` },
    body: JSON.stringify({
      count,
      sample_format: `float32-le-${channels.length}ch-${sps}sps`,
      channels,
      sps,
      started_at: startedAt,
    }),
  };
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const r = await fetch(url, init);
      if (r.ok) return true;
      if (r.status >= 400 && r.status < 500) return false;
    } catch { /* */ }
    if (attempt === 0) await new Promise(r => setTimeout(r, 1500));
  }
  return false;
}

/**
 * Pick a sub-app URL for the case-side iframe.
 *
 * Phase A (2026-05-09): visiomynd is the always-on base layer per user spec —
 * once `phase >= live`, the case iframe loads `cognihome-rppg.pages.dev` (the
 * forked rPPG sub-app) by default, regardless of what source/game the trainer
 * has touched. rPPG runs continuously for the whole session; trainer sees
 * live HR/HRV/coherence in the right rail.
 *
 * Mock sub-apps (`/subapp/*-demo`) remain reachable for protocol testing on
 * staging via explicit source/game selection; production prefers the real
 * fork. Other sub-app forks (myndscape / soramynd / sgimacog) will be wired
 * in Phase B.
 *
 * Returns null only during lobby / ended phases (no monitoring). The audit's
 * H4 fix (no fake-game fallback) is preserved for any source/game that maps
 * to a mock-only path.
 */
// 2026-05-09 audit A1 — Mixed routing:
//   - rppg + soramynd: reverse-proxied through /sub/<fork>/* (same-origin)
//     so screen-mirror DOM capture works.
//   - myndscape: stays cross-origin at cognihome-myndscape.pages.dev because
//     themynd uses Babel-standalone + unpkg + 'unsafe-eval', which the
//     parent's tighter CSP would intersect-deny when same-origin proxied.
//     Trade-off: trainer can't DOM-mirror myndscape's task UI, but
//     cognitive-task progress is observable via subapp_event events log
//     + accuracy / RT metrics in the right rail. Acceptable for MVP.
function getCognihomeRppgUrl(): string      { return "/sub/rppg"; }
function getCognihomeSoramyndUrl(): string  { return "/sub/soramynd"; }

function getCognihomeMyndscapeUrl(): string {
  if (typeof window === "undefined") return "https://cognihome-myndscape.pages.dev";
  const h = window.location.hostname;
  if (h.startsWith("staging.") || h === "localhost" || h === "127.0.0.1") {
    return "https://cognihome-myndscape-staging.pages.dev";
  }
  return "https://cognihome-myndscape.pages.dev";
}

function pickSubapp(
  source: string | null,
  game: string | null,
  room: string,
  phase: "lobby" | "live" | "paused" | "ended" = "lobby",
  activeTask?: string | null,
  taskState?: TaskState,
): string | null {
  const params = `telerehab=1&room=${room}`;
  if (phase === "lobby" || phase === "ended") return null;

  // Phase B priority: when trainer has armed a myndscape task, the case
  // iframe switches to cognihome-myndscape so the case can do the task.
  // While taskState=armed they see "等候訓練師開始"; on start_task →
  // running they actually do the task. Once completed and the trainer
  // clears it back to null, we fall back to visiomynd auto-monitoring.
  // Note: this means continuous rPPG pauses during the task (browser
  // can't share webcam between two iframes on most platforms). Resumes
  // when iframe switches back. P7 hardening item: keep rPPG running in
  // a hidden second iframe — requires browser webcam-sharing investigation.
  if (activeTask?.startsWith("myndscape:") &&
      (taskState === "armed" || taskState === "running" || taskState === "completed")) {
    return `${getCognihomeMyndscapeUrl()}/?${params}`;
  }
  if (activeTask?.startsWith("soramynd:") &&
      (taskState === "armed" || taskState === "running" || taskState === "completed")) {
    return `${getCognihomeSoramyndUrl()}/?${params}`;
  }

  // Phase A default: visiomynd auto-load when no specific task selected.
  // The user's operational spec (memory project_cognihome.md) makes rPPG
  // the always-on base for every session.
  if (game === null && (source === null || source === "visiomynd" || source === "hrv")) {
    return `${getCognihomeRppgUrl()}/?${params}`;
  }

  // Trainer explicitly picked a legacy mock-targeting source/game. Mock
  // paths remain for protocol-level testing on staging; in prod they'll
  // 404 via DemoBanner.isDemoHostAllowed.
  if (game === "soramynd-plane" || game === "soramynd-baseball" ||
      game === "soramynd-zentangle" || game === "soramynd-karesansui") {
    return `/subapp/sora-demo?${params}&game=${game}`;
  }
  if (game === "visiomynd-breath") return `${getCognihomeRppgUrl()}/?${params}`;
  if (game === "myndscape-task")   return `${getCognihomeMyndscapeUrl()}/?${params}`;
  if (game === "velomynd-acpt") {
    // VeloMynd ACPT is not yet wired (cross-repo deferred). Refuse to
    // route through a stand-in game whose metrics would be unrelated.
    return null;
  }
  if (game === null) {
    if (source === "behavior") return `${getCognihomeMyndscapeUrl()}/?${params}`;
    if (source === "baseline") return `/subapp/sgimacog-demo?${params}`;
  }
  return null;
}

/**
 * Case-side placeholder when no sub-app is configured. Renders in the
 * full iframe slot so the case window is never blank — they see the
 * design-locked Quiet Editorial layout while the trainer configures.
 */
function CaseAwaitingConfig() {
  return (
    <div style={{
      position: "absolute", inset: 0,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "var(--ink)", color: "var(--paper)",
      padding: "var(--s-7)",
    }}>
      <div className="serial" style={{
        color: "var(--paper-deep)", marginBottom: "var(--s-7)",
      }}>
        SESSION · STANDBY
      </div>
      <h1 className="italic-display" style={{
        fontSize: "var(--t-2xl)", marginBottom: "var(--s-3)",
        textAlign: "center",
      }}>
        等候<i>訓練師配置</i>
      </h1>
      <p className="muted" style={{
        maxWidth: 480, textAlign: "center",
        color: "var(--paper-deep)", lineHeight: 1.6,
      }}>
        訓練師正在挑選訊號來源與訓練方式。確認後此處會自動顯示對應的訓練畫面。
      </p>
    </div>
  );
}

function useElapsed(startedAt?: string): string {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!startedAt) return "—";
  const start = new Date(startedAt).getTime();
  if (isNaN(start)) return "—";
  const ms = Math.max(0, now - start);
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}
